<template>
  <v-skeleton-loader
    v-if="loading"
    class="m-auto"
    type="table"
    transition="scale-transition"
  />
  <TableRemote
    v-else
    :iconHeader="hasPermission('analyses.types.add') ? 'add' : null"
    :items="displayedTypes"
    :isSearchable="false"
    :headers="tableHeaders"
    :disablePagination="true"
    :title="$t('analyses.types.pageName')"
    :pagination="pagination"
    :centerColumns="['active']"
    :notSortableColumns="['active', 'key', 'formula', 'action']"
    :sortableColumns="['name', 'type', 'category', 'drag_drop']"
    hideDefaultFooter
    class="drag-container"
    @iconAction="onClickAction"
    @sendQueryParams="fetchQuery"
  >
    <template #body="{items}">
      <draggable
        :list="items"
        :options="{disabled: isDraggingDisabled}"
        tag="tbody"
        @change="onDragChange"
      >
        <tr
          v-for="(item,index) in items"
          :key="index"
        >
          <template v-for="(headerN,indexN) in tableHeaders">
            <td
              v-if="headerN.value === 'drag_drop'"
              :key="`${index}_${indexN}_drag`"
            >
              <template>
                <v-icon
                  v-if="!isDraggingDisabled"
                  large
                  class="hidden-md-and-down"
                  color="primary"
                >
                  mdi-drag-vertical
                </v-icon>
                <span @click="activateTooltip(item.displayOrder)"> {{ item.displayOrder }} </span>
              </template>
            </td>
            <td
              v-else-if="headerN.value==='action'"
              :key="`${index}_${indexN}_action`"
            >
              <template v-if="itemActions">
                <div class="action-buttons">
                  <VTableAction
                    :itemActions="itemActions"
                    :verticalActionDots="true"
                    @iconAction="(action)=>onClickAction(item, action)"
                  />
                </div>
              </template>
            </td>
            <td
              v-else-if="headerN.value==='name'"
              :key="`${index}_${indexN}_others`"
            >
              <span>{{ item[headerN.value] }}</span>
              <div
                v-if="isTooltipActive(item.displayOrder)"
                class="custom-drag-input"
              >
                <div class="wrapper-div">
                  <v-text-field
                    v-model="customDragItemPosition"
                    @keydown.enter="onManualPositionInput(item)"
                  />
                </div>
              </div>
            </td>
            <td
              v-else-if="headerN.value === 'active'"
              :key="`${index}_${indexN}_active`"
              class="text-center"
            >
              <v-icon v-if="item.active">
                check
              </v-icon>
            </td>
            <td
              v-else-if="headerN.value === 'formula'"
              :key="`${index}_${indexN}_formula`"
              class="text-center"
            >
              <v-btn
                v-if="item.formula"
                icon
                @click="onClickFormula(item)"
              >
                <v-img
                  src="@/assets/images/fluent_math-formula-24-filled.svg"
                  maxWidth="25"
                  maxHeight="25"
                  class="rounded-circle"
                />
              </v-btn>
            </td>
            <td
              v-else
              :key="`${index}_${indexN}_others`"
            >
              {{ item[headerN.value] }}
            </td>
          </template>
        </tr>
      </draggable>
    </template>
    <template #SearchWithFilters>
      <SearchWithFilters
        :filters="filters"
        :searchPlaceholder="$t('laboratory.placeholder.searchBy')"
        :datesRange="true"
        :filterName="filterName"
        :saveFilter="true"
        @onApplyFilters="handleApplyFilters"
        @onSearch="handleSearch"
      />
    </template>
  </TableRemote>
</template>
<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { cloneDeep, isPlainObject, debounce } from 'lodash';
import VTableAction from '@/components/shared/widgets/VTableAction';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import draggable from 'vuedraggable';
import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import sampleTypes from '@/constants/sampleTypes';
import {
    PRODUCT_CONSUMPTION_ANALYSIS_TYPE,
    WATER_CONSUMPTION_ANALYSIS_TYPE,
    CALCULATED_ANALYSIS_TYPE,
    SIMPLE_ANALYSIS_TYPE,
    TYPE_COMPOSITION,
    TYPE_LEGIONELLA,
    TYPE_CORROSION,
    TYPE_TEXT,
    TYPE_LIST,
    FUEL_CONSUMPTION_ANALYSIS_TYPE,
} from '@/aquassist-shared/constants/types/componentAnalysisConfiguration';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import TypeForm from './typeForm/TypeForm.vue';
import TypeFormulaForm from './typeForm/TypeFormulaForm.vue';


export default {
    components: {
        TableRemote,
        VTableAction,
        draggable,
        SearchWithFilters,
    },
    data() {
        return {
            debouncedSendQueryParams: debounce(this.fetchQuery, 500),
            types: [],
            loading: false,
            isDraggingDisabled: false,
            showTooltipIndex: -1,
            customDragItemPosition: 0,
            searchInput: '',
            categories: [],
            filterName: 'analysesTypes',
            pagination: {},
            queryParams: {
                // setting displaying
                order: 'drag_drop_asc',
                pagination: false,
                // filters
                includeInactive: false,
                search: '',
                categories: [],
                massBalance: false,
                typeName: 'All',
            },
            filters: null,
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'hasPermission',
            'hasRole',
            'teamMainLang',
            'lang',
        ]),
        categoriesTypes() {
            return [
                PRODUCT_CONSUMPTION_ANALYSIS_TYPE,
                WATER_CONSUMPTION_ANALYSIS_TYPE,
                CALCULATED_ANALYSIS_TYPE,
                SIMPLE_ANALYSIS_TYPE,
                TYPE_COMPOSITION,
                TYPE_LEGIONELLA,
                TYPE_CORROSION,
                TYPE_TEXT,
                TYPE_LIST,
                FUEL_CONSUMPTION_ANALYSIS_TYPE,
            ];
        },
        sampleTypesList() {
            const sampleTypesList = Object.values(sampleTypes).map(sType => ({
                text: `sampleTypes.${sType.charAt(0).toLowerCase()}${sType.substring(1)}`,
                value: sType,
            }));
            return sampleTypesList;
        },
        tableHeaders() {
            const header = [
                {
                    text: '',
                    value: 'drag_drop',
                    sortable: '',
                },
                {
                    text: this.$t('forms.labels.name'),
                    value: 'name',
                },
                {
                    text: this.$t('forms.labels.type'),
                    value: 'type',
                },
                {
                    text: this.$t('forms.labels.category'),
                    value: 'category',
                },
                {
                    text: this.$t('forms.labels.key'),
                    value: 'key',
                },
                {
                    text: this.$t('forms.labels.active'),
                    value: 'active',
                    width: '10%',
                },
                {
                    text: this.$t('forms.labels.formula'),
                    value: 'formula',
                },
                {
                    text: this.$t('controls.actions'),
                    value: 'action',
                },
            ];
            return header;
        },
        displayedTypes() {
            return this.types.map(analysisType => {
                const { id, active, name, type, category, displayOrder, deviceAnalysisKey, formula, variablesName } = analysisType;
                const categoryLabel = isPlainObject(category) ? category.name[this.userLang] || category.name[this.teamMainLang] : '-';
                return {
                    id,
                    active,
                    name: name[this.userLang] ? name[this.userLang] : name[this.teamMainLang],
                    type: this.$t(`analyses.types.${type}`),
                    category: categoryLabel,
                    displayOrder,
                    key: deviceAnalysisKey?.key,
                    formula,
                    variablesName,
                };
            });
        },
        itemActions() {
            const actions = [];
            if (this.hasPermission('analyses.types.edit')) {
                actions.push({ name: 'edit', icon: 'mdi-pencil', action: 'edit' });
            } else {
                return [];
            }
            return actions;
        },
        includeInactiveBtn() {
            return this.hasRole('manager') || this.hasRole('administrator');
        },
    },
    watch: {
        userLang() {
            this.filters = this.filtersCustom();
        },
    },
    async created() {
        this.loading = true;
        await this.getTypes();
        if (this.searchInput?.length) {
            this.queryParams.search = this.searchInput;
        }
        await this.getCategories();
        this.filters = this.filtersCustom();
        this.loading = false;
    },
    methods: {
        async getTypes() {
            const paramsPayload = cloneDeep(this.queryParams);
            const { data } = await this.$rq.getAnalysisTypes(paramsPayload);
            this.types = data.types;
            this.pagination = data.pagination;
            this.loading = false;
            this.isDraggingDisabled = this.searchInput?.length > 0;
            this.showTooltipIndex = -1;
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('analyses.types.add'),
                showLangs: true,
                isForm: false,
                showNextPrevious: true,
                width: 1500,
                destroy: false,
                component: Vue.component('TypeForm', TypeForm),
                onConfirm: async requestPayload => {
                    await this.addType(requestPayload);
                    this.$root.$emit('destroyModal');
                },
            });
        },
        isTooltipActive(currentIndex) {
            return this.showTooltipIndex === currentIndex;
        },
        activateTooltip(currentIndex) {
            this.showTooltipIndex = currentIndex;
            this.customDragItemPosition = currentIndex;
        },

        onManualPositionInput(item) {
            if (this.customDragItemPosition !== this.showTooltipIndex) {
                this.onDragChange({ moved: { element: item, newIndex: this.customDragItemPosition } });
            }
        },
        async onDragChange(e) {
            const { element: analysisType, newIndex } = e.moved;
            await this.$rq.updateAnalysisTypePosition(analysisType.id, { newIndex });
            this.getTypes();
        },
        async onClickAction(type, action) {
            if (type === 'add' && !action) {
                this.onClickAdd();
                return;
            }
            if (action.action === 'edit') {
                const { data } = await this.$rq.getAnalysisType(type.id);
                this.$root.$emit('showModal', {
                    title: this.$t('analyses.types.update'),
                    component: Vue.component('TypeForm', TypeForm),
                    showLangs: true,
                    destroy: false,
                    width: 1500,
                    payload: {
                        analysisType: cloneDeep(data),
                        isEdition: true,
                    },
                    isForm: false,
                    onConfirm: requestPayload => {
                        if (requestPayload) {
                            // validate if the formula was not empty and has changed
                            if (data.formula?.length && data.formula !== requestPayload.formula) {
                                // alerts
                                this.$root.$emit('showModal', {
                                    title: this.$t('generic.confirm'),
                                    destroy: false,
                                    component: Vue.component('ConfirmRemoveFormula', ConfirmForm),
                                    isForm: false,
                                    payload: { msg: this.$t('analyses.types.confirmFormulaChange', { analysisType: data.name[this.lang] || this.$t('analyses.types.generic') }) },
                                    onConfirm: async () => {
                                        await this.updateType(type.id, requestPayload);
                                        this.$root.$emit('destroyModal');
                                    },
                                });
                            } else {
                                this.updateType(type.id, requestPayload);
                            }
                        }
                    },
                });
            }
        },
        async addType(requestPayload) {
            const cleanPayload = this.$utils.removeEmptyNullOrUndefined(requestPayload, true);
            await this.$rq.addAnalysisType(cleanPayload);
            this.$toasted.global.toast_success({
                message: this.$t('analyses.types.added', { name: requestPayload.name[this.userLang] }),
            });
            await this.getTypes();
        },
        async updateType(id, requestPayload) {
            const cleanPayload = cloneDeep(requestPayload);
            if (cleanPayload.deviceAnalysisKey && typeof cleanPayload.deviceAnalysisKey === 'object') {
                cleanPayload.deviceAnalysisKey = cleanPayload.deviceAnalysisKey.id;
            }
            if (!cleanPayload.massBalance && cleanPayload.componentTypes) cleanPayload.componentTypes = [];
            await this.$rq.updateAnalysisType(id, cleanPayload);
            this.$toasted.global.toast_success({
                message: this.$t('analyses.types.updated', { name: requestPayload.name[this.userLang] }),
            });
            await this.getTypes();
        },
        async fetchQuery({ order }) {
            this.queryParams.order = order;
            this.getTypes();
        },
        handleSearch(search) {
            this.queryParams = {
                ...this.queryParams,
                search,
            };
            const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
            this.queryParams = cleanQueryParams;
            this.types = [];
            this.getTypes();
        },
        handleApplyFilters(filters) {
            this.queryParams = {
                ...filters,
                search: this.queryParams.search || '',
                order: this.queryParams.order || 'drag_drop_asc',
                pagination: false,
            };
            this.getTypes();
        },

        async getCategories() {
            const { data } = await this.$rq.getAnalysisCategories();
            this.categories = data.categories;
        },
        filtersCustom() {
            return [
                {
                    key: 'includeInactive',
                    label: 'analysisRequests.labels.includeInactive',
                    type: 'switch',
                    value: false,
                    datacy: 'analysisTypeIncludeInactiveFilter',
                },
                {
                    key: 'typeName',
                    label: 'analysisRequests.labels.types',
                    type: 'select',
                    value: 'All',
                    multiple: false,
                    items: Object.values(this.categoriesTypes).map(sType => ({
                        text: this.$t(`analyses.types.${sType}`),
                        value: sType,
                    })),
                    datacy: 'AnalysisTypeTypeFilter',
                },
                {
                    key: 'categories',
                    label: 'forms.labels.category',
                    type: 'select',
                    value: undefined,
                    multiple: true,
                    items: Object.values(this.categories).map(category => ({
                        text: `${category.name[this.userLang] || category.name[this.teamMainLang]}`,
                        value: category.id,
                    })),
                    datacy: 'AnalysisTypeCategoryFilter',
                },
                {
                    key: 'massBalance',
                    label: 'analysisRequests.labels.massBalance',
                    type: 'checkbox',
                    value: false,
                    items: [
                        { text: 'analysisRequests.labels.includeInactive', value: true },
                        { text: 'analysisRequests.labels.notIncludeInactive', value: false },
                    ],
                    datacy: 'AnalysisTypeMassBalanceFilter',
                },
            ];
        },
        async onClickFormula(item) {
            const analysisType = { ...item };
            if (!item.variablesName || !item.variablesName.length) return;
            const variableNameDetails = [];
            // eslint-disable-next-line no-restricted-syntax
            for (const vn of item.variablesName) {
                let at = {};
                at = this.types.find(t => t.id === vn.ref);
                if (!at) {
                    // in case analysis type of variables not in the types list (for filter)
                    // eslint-disable-next-line no-await-in-loop
                    const { data } = await this.$rq.getAnalysisType(vn.ref);
                    at = data;
                }
                variableNameDetails.push({
                    variableName: vn.name,
                    analysisType: at,
                });
            }
            analysisType.variablesName = variableNameDetails;

            this.$root.$emit('showModal', {
                title: `${this.$t('forms.labels.formula')} : ${analysisType.name[this.userLang] || analysisType.name[this.teamMainLang]}`,
                component: Vue.component('FormulaForm', TypeFormulaForm),
                payload: { analysisType },
                showConfirmButton: false,
            });
        },
    },
};
</script>
<style lang='scss' scoped>
  .custom-drag-input {
        position: relative;
        width: 18%;
        .wrapper-div{
          position: absolute;
          top: 8px;
          height: 33px;
          padding: 0px 5px;
          outline: 1px solid;
          background-color: white;
          .v-text-field{
            padding-top: 0px;
            margin-top: 0px;
          }
        }
      }

  @media screen and (max-width: 800px) {
    .drag-container {
      overflow: scroll ;
      margin-left: 55px !important;
      padding-left: 0px;
    }
  }

</style>
